
import React, { useCallback, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ImagePlus, X, Eye, EyeOff, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, STORAGE_URL } from "@/lib/supabase";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface AlertImage {
  id: string;
  filename: string;
  storage_path: string;
  width: number;
  height: number;
  aspect_ratio: number;
}

interface KitImageConfig {
  id: string;
  kit_id: string;
  image_id: string;
  display_order: number;
  duration: number;
  is_active: boolean;
}

interface AlertImageControlProps {
  kitId: string | null;
}

export const AlertImageControl: React.FC<AlertImageControlProps> = ({ kitId }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<AlertImage | null>(null);

  const { data: images = [], error: imagesError } = useQuery<AlertImage[]>({
    queryKey: ["alert-images"],
    queryFn: async () => {
      console.log('Fetching alert images from database');
      const { data, error } = await supabase
        .from("alert_images")
        .select("*")
        .order("created_at", { ascending: true });
      
      if (error) {
        console.error('Error fetching images:', error);
        throw error;
      }
      
      console.log('Retrieved images:', data);
      return data;
    },
  });

  if (imagesError) {
    console.error('Error in images query:', imagesError);
  }

  const { data: kitConfigs = [], error: kitConfigsError } = useQuery<KitImageConfig[]>({
    queryKey: ["kit-image-configs", kitId],
    queryFn: async () => {
      if (!kitId) return [];
      
      console.log('Fetching kit configurations for kit:', kitId);
      const { data, error } = await supabase
        .from("kit_image_configs")
        .select("*")
        .eq("kit_id", kitId)
        .order("display_order", { ascending: true });
      
      if (error) {
        console.error('Error fetching kit configs:', error);
        throw error;
      }
      
      console.log('Retrieved kit configs:', data);
      return data;
    },
    enabled: !!kitId,
  });

  if (kitConfigsError) {
    console.error('Error in kit configs query:', kitConfigsError);
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      console.log('Selected file:', file.name, 'Type:', file.type);
      
      if (!file.type.includes('webp')) {
        toast({
          title: "Invalid file type",
          description: "Please upload a WebP image",
          variant: "destructive",
        });
        return;
      }

      try {
        setUploading(true);
        const reader = new FileReader();
        reader.onloadend = async () => {
          console.log('File read successfully');
          const img = new Image();
          img.onload = async () => {
            console.log('Image dimensions:', img.width, 'x', img.height);
            await uploadImageMutation.mutateAsync({
              file,
              width: img.width,
              height: img.height,
            });
          };
          img.onerror = (error) => {
            console.error('Error loading image:', error);
            toast({
              title: "Error",
              description: "Failed to load image",
              variant: "destructive",
            });
          };
          img.src = reader.result as string;
        };
        reader.onerror = (error) => {
          console.error('Error reading file:', error);
          toast({
            title: "Error",
            description: "Failed to read file",
            variant: "destructive",
          });
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error in file selection:', error);
        toast({
          title: "Error",
          description: "Failed to process file",
          variant: "destructive",
        });
      } finally {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const uploadImageMutation = useMutation({
    mutationFn: async ({ file, width, height }: { file: File; width: number; height: number }) => {
      console.log('Starting file upload to Supabase');
      const timestamp = new Date().getTime();
      const fileExt = file.name.split('.').pop();
      const filePath = `${timestamp}.${fileExt}`;
      const aspectRatio = width / height;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('alert_images')
        .upload(filePath, file);
      
      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }
      
      console.log('File uploaded successfully:', filePath);

      const { data: dbData, error: dbError } = await supabase.from('alert_images').insert([
        {
          filename: file.name,
          storage_path: filePath,
          width,
          height,
          aspect_ratio: aspectRatio,
        },
      ]).select();

      if (dbError) {
        console.error('Database insert error:', dbError);
        throw dbError;
      }

      console.log('Database record created successfully');
      return { uploadData, dbData };
    },
    onSuccess: (data) => {
      console.log('Upload mutation completed successfully:', data);
      queryClient.invalidateQueries({ queryKey: ["alert-images"] });
      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
    },
    onError: (error) => {
      console.error('Upload mutation error:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive",
      });
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (config: Partial<KitImageConfig> & { id: string }) => {
      const { error } = await supabase
        .from("kit_image_configs")
        .update(config)
        .eq("id", config.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kit-image-configs", kitId] });
    },
  });

  const addToTimelineMutation = useMutation({
    mutationFn: async (imageId: string) => {
      if (!kitId) throw new Error("No kit selected");
      const maxOrder = Math.max(...kitConfigs.map(c => c.display_order), -1) + 1;
      const { error } = await supabase.from("kit_image_configs").insert([
        {
          kit_id: kitId,
          image_id: imageId,
          display_order: maxOrder,
          duration: 5000,
          is_active: true,
        },
      ]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kit-image-configs", kitId] });
      toast({
        title: "Success",
        description: "Image added to timeline",
      });
    },
    onError: (error) => {
      console.error('Add to timeline error:', error);
      toast({
        title: "Error",
        description: "Failed to add image to timeline",
        variant: "destructive",
      });
    },
  });

  const removeFromTimelineMutation = useMutation({
    mutationFn: async (configId: string) => {
      const { error } = await supabase
        .from("kit_image_configs")
        .delete()
        .eq("id", configId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kit-image-configs", kitId] });
      toast({
        title: "Success",
        description: "Image removed from timeline",
      });
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: async (image: AlertImage) => {
      console.log('Deleting image:', image.filename);
      
      const { error: storageError } = await supabase.storage
        .from('alert_images')
        .remove([image.storage_path]);
      
      if (storageError) {
        console.error('Storage delete error:', storageError);
        throw storageError;
      }

      const { error: dbError } = await supabase
        .from('alert_images')
        .delete()
        .eq('id', image.id);
      
      if (dbError) {
        console.error('Database delete error:', dbError);
        throw dbError;
      }

      return image;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alert-images"] });
      toast({
        title: "Success",
        description: "Image deleted successfully",
      });
      setImageToDelete(null);
    },
    onError: (error) => {
      console.error('Delete mutation error:', error);
      toast({
        title: "Error",
        description: "Failed to delete image",
        variant: "destructive",
      });
      setImageToDelete(null);
    },
  });

  const getImageUrl = (path: string) => {
    const url = `${STORAGE_URL}/storage/v1/object/public/alert_images/${path}`;
    console.log('Generated image URL:', url);
    return url;
  };

  return (
    <Card className="p-4 space-y-4 bg-ticker-bg text-ticker-text">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Alert Images</h3>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".webp"
          onChange={handleFileSelect}
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-ticker-accent hover:bg-ticker-accent/90"
        >
          <ImagePlus className="h-4 w-4 mr-2" />
          Upload WebP
        </Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {images.map((image) => (
          <div key={image.id} className="relative group">
            <AspectRatio ratio={3.91}>
              <img
                src={getImageUrl(image.storage_path)}
                alt={image.filename}
                className="w-full h-full object-cover rounded cursor-pointer hover:opacity-75 transition-opacity"
                onClick={() => {
                  if (kitId && !kitConfigs.some(c => c.image_id === image.id)) {
                    addToTimelineMutation.mutate(image.id);
                  }
                }}
              />
            </AspectRatio>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => setImageToDelete(image)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#1A1F2C] border border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-300">
                    This action cannot be undone. This will permanently delete the image
                    "{image.filename}" and remove it from any kits using it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-gray-700 hover:bg-gray-600 text-white border-none">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteImageMutation.mutate(image)}
                    className="bg-red-500 hover:bg-red-600 text-white"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <p className="mt-1 text-xs text-gray-400 truncate">{image.filename}</p>
          </div>
        ))}
      </div>

      {kitId && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold mb-4">Timeline</h3>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {kitConfigs.map((config) => {
              const image = images.find(img => img.id === config.image_id);
              if (!image) return null;

              return (
                <div
                  key={config.id}
                  className="flex flex-col gap-2 min-w-[200px] bg-ticker-muted/20 p-4 rounded"
                >
                  <div className="relative group">
                    <AspectRatio ratio={3.91}>
                      <img
                        src={getImageUrl(image.storage_path)}
                        alt={image.filename}
                        className="w-full h-full object-cover rounded"
                      />
                    </AspectRatio>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-red-500/20 hover:bg-red-500/40 text-white"
                      onClick={() => removeFromTimelineMutation.mutate(config.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`active-${config.id}`}
                          checked={config.is_active}
                          onCheckedChange={(checked) =>
                            updateConfigMutation.mutate({
                              id: config.id,
                              is_active: checked as boolean,
                            })
                          }
                        />
                        <label
                          htmlFor={`active-${config.id}`}
                          className="text-sm flex items-center gap-1"
                        >
                          {config.is_active ? (
                            <>
                              <Eye className="h-3 w-3" /> Visible
                            </>
                          ) : (
                            <>
                              <EyeOff className="h-3 w-3" /> Hidden
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm">
                        Duration: {(config.duration / 1000).toFixed(1)}s
                      </label>
                      <Slider
                        value={[config.duration]}
                        onValueChange={([value]) =>
                          updateConfigMutation.mutate({
                            id: config.id,
                            duration: value,
                          })
                        }
                        min={1000}
                        max={10000}
                        step={100}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};
